import { useFormikContext } from "formik";
import { AGENT_SKILL_TITLE_MAX_CHARS } from "@common/constants/agentSkill";
import {
    AgentSkillCategory,
    AgentSkillKind,
    AgentSkillLoadMode,
} from "@common/types/agentSkill";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { HStack } from "@design-system/HStack/HStack";
import { Tag } from "@design-system/Tag/Tag";
import { VStack } from "@design-system/VStack/VStack";
import { AgentSkillBodyField } from "./AgentSkillBodyField";
import {
    AGENT_SKILL_CATEGORY_LABELS,
    AGENT_SKILL_LOAD_MODE_DESCRIPTIONS,
    AGENT_SKILL_LOAD_MODE_LABELS,
    FIRM_SCOPE_LABEL,
} from "./agentSkillCopy";
import {
    AgentSkillFormValues,
    agentSkillDescriptionMaxChars,
} from "./agentSkillForm";
import styles from "./AgentSkills.module.scss";

const fields = createFormikFields<AgentSkillFormValues>();

const CATEGORY_OPTIONS = Object.values(AgentSkillCategory).map((value) => ({
    value,
    label: AGENT_SKILL_CATEGORY_LABELS[value],
}));

const LOAD_MODE_OPTIONS = Object.values(AgentSkillLoadMode).map((value) => ({
    value,
    label: AGENT_SKILL_LOAD_MODE_LABELS[value],
    description: AGENT_SKILL_LOAD_MODE_DESCRIPTIONS[value],
}));

interface Props {
    clientName: string | null;
    kind: AgentSkillKind;
    /** The tools a Kick skill attaches; the text may name them freely. */
    tools: string[];
}

/** The create drawer's fields: metadata inputs above the styled body editor. */
export const AgentSkillFormFields = ({ clientName, kind, tools }: Props) => {
    const { initialValues } = useFormikContext<AgentSkillFormValues>();
    const isKick = kind === AgentSkillKind.KICK;

    return (
        <VStack gap={16}>
            {!isKick && (
                <HStack gap={8} align="center">
                    <span className="text-secondary">Scope</span>
                    <Tag color="neutral" size="sm" outline>
                        {clientName ?? FIRM_SCOPE_LABEL}
                    </Tag>
                </HStack>
            )}
            <fields.Text
                name="title"
                label="Title"
                size="lg"
                maxLength={AGENT_SKILL_TITLE_MAX_CHARS}
                autoFocus
            />
            <fields.TextArea
                name="description"
                label="Description"
                size="lg"
                autoGrow
                minRows={2}
                maxLength={agentSkillDescriptionMaxChars(kind)}
            />
            {!isKick && (
                <HStack gap={16} align="start">
                    <fields.Select
                        name="category"
                        label="Category"
                        options={CATEGORY_OPTIONS}
                    />
                    <fields.Select
                        name="loadMode"
                        label="Frequency"
                        options={LOAD_MODE_OPTIONS}
                    />
                </HStack>
            )}
            <AgentSkillBodyField
                markdown={initialValues.body}
                revisionId={null}
                isEditable
                kind={kind}
                className={styles.preview}
            />
            {isKick && tools.length > 0 && (
                <VStack gap={4}>
                    <span className="text-secondary">
                        Tools this skill attaches; the text may name them.
                    </span>
                    <HStack gap={4} wrap>
                        {tools.map((tool) => (
                            <Tag key={tool} color="neutral" size="sm" outline>
                                {tool}
                            </Tag>
                        ))}
                    </HStack>
                </VStack>
            )}
        </VStack>
    );
};
